import { useCallback, useState } from "react"

/** Prefix a bare host with https:// so window.open and url() validation work. */
export function normalizeUrl(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return trimmed
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

/**
 * Opens a list of URLs as new tabs and reports which ones the browser blocked.
 *
 * Note: we intentionally do NOT pass "noopener"/"noreferrer" — per spec those
 * make window.open return null, which would make every tab look "blocked" and
 * defeat the detection. These are user-curated links in an internal tool, so
 * the opener exposure is an acceptable trade for reliable block detection.
 */
export function useOpenTabs() {
  const [blocked, setBlocked] = useState<string[]>([])

  const openAll = useCallback((urls: string[]): string[] => {
    const blockedUrls: string[] = []
    for (const raw of urls) {
      const url = normalizeUrl(raw)
      if (!url) continue
      const win = window.open(url, "_blank")
      if (!win || win.closed || typeof win.closed === "undefined") {
        blockedUrls.push(url)
      }
    }
    setBlocked(blockedUrls)
    return blockedUrls
  }, [])

  const clearBlocked = useCallback(() => setBlocked([]), [])

  return { openAll, blocked, clearBlocked }
}
